import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  BookOpen,
  Code2,
  Compass,
  LayoutDashboard,
  Lock,
  Scale,
  School,
  Sparkles,
  Target
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import useBackgroundImagePreload from '../hooks/useBackgroundImagePreload';

const featureCards = [
  {
    id: 'blogs',
    title: 'Student Blogs & Guidance',
    description: 'Read real student stories, career insights, and practical college guidance before you decide.',
    icon: BookOpen,
    route: '/blogs',
    audience: 'public',
    badge: 'Public',
    ctaLabel: 'Explore blogs',
    eyebrow: 'Open Access',
    accentClass: 'from-[#7f3df1] via-[#b44fb2] to-[#ff7f32]',
    surfaceClass: 'from-white via-[#fbf6ff] to-[#fff3eb]',
    ringClass: 'border-[#efdaf9]',
    points: ['Fresh student stories', 'Career-focused reads', 'SEO-friendly public content']
  },
  {
    id: 'college-discovery',
    title: 'College Discovery',
    description: 'Browse college options, review highlights, and start narrowing down the right fit for your goals.',
    icon: School,
    route: '/college-profile',
    audience: 'private',
    badge: 'Sign up to unlock',
    ctaLabel: 'View colleges',
    eyebrow: 'Decision Support',
    accentClass: 'from-[#ff8a3d] via-[#ff6b6b] to-[#f04d8f]',
    surfaceClass: 'from-white via-[#fff8f1] to-[#fff0f5]',
    ringClass: 'border-[#ffe2ce]',
    points: ['College highlights', 'Search-focused discovery', 'Private full experience']
  },
  {
    id: 'compare',
    title: 'Compare Colleges',
    description: 'Put colleges side by side to evaluate options faster instead of switching between multiple tabs.',
    icon: Scale,
    route: '/comparator',
    audience: 'private',
    badge: 'Private tool',
    ctaLabel: 'Start comparing',
    eyebrow: 'Fast Decisions',
    accentClass: 'from-[#6648ff] via-[#8b5cf6] to-[#d946ef]',
    surfaceClass: 'from-white via-[#f7f4ff] to-[#fff5ff]',
    ringClass: 'border-[#e9defc]',
    points: ['Side-by-side comparison', 'Less tab switching', 'Clearer decision making']
  },
  {
    id: 'tutorials',
    title: 'Learning Paths',
    description: 'Move through tutorials and topic-based content with a guided structure built for steady progress.',
    icon: Compass,
    route: '/tutorials',
    audience: 'private',
    badge: 'Private tool',
    ctaLabel: 'Browse tutorials',
    eyebrow: 'Skill Building',
    accentClass: 'from-[#0ea5e9] via-[#3b82f6] to-[#7c3aed]',
    surfaceClass: 'from-white via-[#f0f9ff] to-[#f7f0ff]',
    ringClass: 'border-[#d5ebff]',
    points: ['Structured paths', 'Topic navigation', 'Progress-based learning']
  },
  {
    id: 'compiler',
    title: 'Practice With Compiler',
    description: 'Test code, experiment with examples, and learn faster with the built-in coding workspace.',
    icon: Code2,
    route: '/compiler',
    audience: 'private',
    badge: 'Private tool',
    ctaLabel: 'Open compiler',
    eyebrow: 'Hands-On Practice',
    accentClass: 'from-[#0f172a] via-[#334155] to-[#7f3df1]',
    surfaceClass: 'from-white via-[#f6f7fb] to-[#f6f1ff]',
    ringClass: 'border-[#dde3f1]',
    points: ['Code and test quickly', 'Learning by doing', 'Built into the platform']
  },
  /*
  Practice Arena promo hidden for now.
  {
    id: 'practice-arena',
    title: 'Practice Arena',
    description: 'Train typing speed with timed coding and English rounds, daily challenges, and instant accuracy feedback.',
    icon: Timer,
    route: '/practice-arena',
    audience: 'public',
    badge: 'Public',
    ctaLabel: 'Open practice arena',
    eyebrow: 'Speed Training',
    accentClass: 'from-[#0f172a] via-[#1d4ed8] to-[#f97316]',
    surfaceClass: 'from-white via-[#f5f9ff] to-[#fff6ef]',
    ringClass: 'border-[#d9e3ff]',
    points: ['Timed typing rounds', 'Daily challenge mode', 'Coding and English practice']
  },
  */
  {
    id: 'dashboard',
    title: 'Personal Dashboard',
    description: 'Track your journey, manage your progress, and keep all your personalized activity in one place.',
    icon: LayoutDashboard,
    route: '/dashboard',
    audience: 'private',
    badge: 'Private area',
    ctaLabel: 'Go to dashboard',
    eyebrow: 'Your Hub',
    accentClass: 'from-[#22c55e] via-[#14b8a6] to-[#3b82f6]',
    surfaceClass: 'from-white via-[#f0fff7] to-[#eef8ff]',
    ringClass: 'border-[#d9f5e5]',
    points: ['Progress visibility', 'Personalized flow', 'Everything in one place']
  }
];

const FeatureShowcaseSection = () => {
  const { isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const backgroundImageSrc = '/landing-bg2.jpeg';
  const isBackgroundLoaded = useBackgroundImagePreload(backgroundImageSrc);
  const mobileScrollRef = useRef(null);
  const [activeIndex, setActiveIndex] = useState(0);

  const handleMobileScroll = () => {
    const el = mobileScrollRef.current;
    if (!el) return;

    const cardWidth = el.scrollWidth / featureCards.length;
    const index = Math.round(el.scrollLeft / cardWidth);
    setActiveIndex(Math.min(index, featureCards.length - 1));
  };

  const scrollToCard = (index) => {
    const el = mobileScrollRef.current;
    if (!el) return;

    const cardWidth = el.scrollWidth / featureCards.length;
    el.scrollTo({ left: cardWidth * index, behavior: 'smooth' });
    setActiveIndex(index);
  };

  const handleFeatureClick = (feature) => {
    if (feature.audience === 'public' || isAuthenticated) {
      navigate(feature.route);
      return;
    }

    navigate('/signup', {
      state: {
        from: {
          pathname: feature.route
        }
      }
    });
  };

  const handleSignInClick = (feature) => {
    navigate('/signin', {
      state: {
        from: {
          pathname: feature.route
        }
      }
    });
  };

  return (
    <section className="relative overflow-hidden px-4 py-8 sm:px-6 lg:px-8 lg:py-10">
      <div
        className={`absolute inset-0 bg-cover bg-center bg-no-repeat transition-opacity duration-500 ${
          isBackgroundLoaded ? 'opacity-100' : 'opacity-0'
        }`}
        style={{ backgroundImage: `url('${backgroundImageSrc}')` }}
      />
      <div className="absolute inset-0 bg-[linear-gradient(180deg,rgba(255,253,249,0.92)_0%,rgba(255,247,239,0.9)_42%,rgba(247,239,255,0.94)_100%)]" />
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(243,111,33,0.18),transparent_24%),radial-gradient(circle_at_bottom_left,rgba(127,61,241,0.16),transparent_28%)]" />
      <div className="absolute left-[-5rem] top-20 h-40 w-40 rounded-full bg-[#ffb27d]/20 blur-3xl" />
      <div className="absolute bottom-8 right-[-4rem] h-48 w-48 rounded-full bg-[#8d4ef0]/18 blur-3xl" />

      <div className="relative mx-auto max-w-7xl">
        <div className="mx-auto max-w-3xl text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-[#efdaf9] bg-white/85 px-4 py-2 text-[10px] font-bold uppercase tracking-[0.22em] text-[#8e43d7] shadow-sm backdrop-blur">
            <Sparkles className="h-3.5 w-3.5" />
            Platform Overview
          </div>
          <h2 className="mt-4 text-2xl font-bold tracking-tight text-[#1f1730] sm:text-3xl lg:text-[2rem]">
            Explore what you can do on Race Next Step
          </h2>
          <p className="mt-2 text-sm leading-6 text-[#5d536b] sm:text-[15px] sm:leading-7">
            Explore blogs, college tools, learning paths, coding practice, and your personal dashboard in one place.
            Start with the public sections and unlock the full experience whenever you&apos;re ready.
          </p>
        </div>

        <div
          ref={mobileScrollRef}
          onScroll={handleMobileScroll}
          className="-mx-4 mt-8 overflow-x-auto px-4 pb-3 sm:-mx-6 sm:px-6 md:mx-0 md:overflow-visible md:px-0 md:pb-0"
          style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <div className="flex snap-x snap-mandatory gap-4 md:grid md:gap-4 md:grid-cols-2 xl:grid-cols-3">
            {featureCards.map((feature) => {
              const Icon = feature.icon;
              const isPrivate = feature.audience === 'private';

              return (
                <article
                  key={feature.id}
                  className={`group relative flex min-h-[380px] w-[85vw] max-w-[340px] shrink-0 snap-center flex-col overflow-hidden rounded-[24px] border ${feature.ringClass} bg-gradient-to-br ${feature.surfaceClass} p-4 shadow-[0_18px_38px_rgba(71,31,123,0.09)] transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_22px_48px_rgba(71,31,123,0.13)] md:min-h-0 md:w-auto md:max-w-none md:shrink`}
                >
                  <div className={`absolute inset-x-0 top-0 h-1 bg-gradient-to-r ${feature.accentClass}`} />
                  <div className="absolute right-[-1.5rem] top-[-1.5rem] h-20 w-20 rounded-full bg-white/40 blur-2xl transition-transform duration-300 group-hover:scale-110" />

                  <div className="relative flex items-start justify-between gap-3">
                    <div className={`flex h-11 w-11 items-center justify-center rounded-[16px] bg-gradient-to-br ${feature.accentClass} text-white shadow-md shadow-[#b07adf]/15`}>
                      <Icon className="h-5 w-5" />
                    </div>
                    <span
                      className={`inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[9px] font-bold uppercase tracking-[0.14em] ${
                        isPrivate
                          ? 'bg-[#fff1e9] text-[#d36625]'
                          : 'bg-[#eef9f2] text-[#2d8a57]'
                      }`}
                    >
                      {isPrivate ? <Lock className="h-3 w-3" /> : null}
                      {feature.badge}
                    </span>
                  </div>

                <div className="relative mt-3">
                  <p className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#8a4be5]">
                    {feature.eyebrow}
                  </p>
                  <h3 className="mt-1.5 text-lg font-bold leading-tight text-[#211731]">{feature.title}</h3>
                  <p className="mt-2 text-sm leading-6 text-[#655a73]">{feature.description}</p>
                </div>

                  <div className="relative mt-3 rounded-[18px] border border-white/85 bg-white/70 p-3 backdrop-blur-sm">
                  <div className="mb-2 flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-[#5d4e76]">
                    <Target className="h-3.5 w-3.5 text-[#8a4be5]" />
                    Why it matters
                  </div>
                  <div className="space-y-1.5">
                    {feature.points.map((point) => (
                      <div key={point} className="flex items-start gap-2 text-[13px] leading-5 text-[#4f4560]">
                          <span className={`mt-1.5 h-2 w-2 shrink-0 rounded-full bg-gradient-to-r ${feature.accentClass}`} />
                          <span>{point}</span>
                      </div>
                    ))}
                  </div>
                </div>

                  <div className="relative mt-4 flex flex-1 flex-col justify-end gap-2.5">
                    <button
                      type="button"
                      onClick={() => handleFeatureClick(feature)}
                      className={`inline-flex items-center justify-center gap-2 rounded-[16px] bg-gradient-to-r ${feature.accentClass} px-4 py-2.5 text-sm font-semibold text-white shadow-[0_12px_24px_rgba(145,70,191,0.18)] transition-transform duration-300 hover:scale-[1.01]`}
                    >
                      {isPrivate && !isAuthenticated ? 'Sign Up to Access' : feature.ctaLabel}
                      <ArrowRight className="h-4 w-4" />
                    </button>

                    {isPrivate && !isAuthenticated ? (
                      <button
                        type="button"
                        onClick={() => handleSignInClick(feature)}
                        className="rounded-[16px] border border-white/85 bg-white/75 px-4 py-2.5 text-sm font-semibold text-[#6f43b2] backdrop-blur-sm transition-colors hover:bg-[#f7f0ff]"
                      >
                        Already have an account? Sign in
                      </button>
                    ) : null}
                  </div>
                </article>
              );
            })}
          </div>
        </div>

        {featureCards.length > 1 ? (
          <div className="mt-4 flex items-center justify-center gap-1.5 md:hidden">
            {featureCards.map((feature, index) => (
              <button
                key={feature.id}
                type="button"
                aria-label={`Go to ${feature.title}`}
                onClick={() => scrollToCard(index)}
                className={`h-1.5 rounded-full transition-all duration-300 ${
                  index === activeIndex
                    ? 'w-5 bg-[#9a51a0]'
                    : 'w-1.5 bg-[#d6c9e7]'
                }`}
              />
            ))}
          </div>
        ) : null}
      </div>
    </section>
  );
};

export default FeatureShowcaseSection;
